import {
    type ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    type Draft,
    type SerializedError,
} from '@reduxjs/toolkit';

import type { IAction, IGlobalStoreReducers } from './initStore';
import initI18n from '../locales';
import { GlobalVariables } from '../pdf/GlobalVariables';
import { backendApiUrl } from '../App';

/**
 * Initial slice state value.
 */
const initialState: IInitSettingsState = {
    data: {
        customFontURL: process.env.REACT_APP_FONTS_URL ?? '',
        documentUrl: process.env.REACT_APP_DOC_URL ?? '',
        lic: process.env.REACT_APP_LIC ?? '',
        language: navigator.language || 'en',
    },
    loading: false,
    error: null,
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Logs the user out of the application.
     */
    getInitSettings: async (): Promise<IInitSettings> => {
        if (GlobalVariables.isVisualStudioCode) {
            return new Promise<IInitSettings>(resolve => {
                setTimeout(() => {
                    resolve({
                        customFontURL: process.env.REACT_APP_FONTS_URL ?? '',
                        documentUrl: process.env.REACT_APP_DOC_URL ?? '',
                        lic: process.env.REACT_APP_LIC ?? '',
                        language: navigator.language || 'en',
                    });
                }, 250);
            });
        }

        try {
            const response = await fetch(backendApiUrl.init);

            const initSettings: IInitSettings = await response.json();

            initI18n(initSettings.language);

            return response.ok ? Promise.resolve(initSettings) : Promise.reject(response.status);
        } catch (e) {
            return Promise.reject(e);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IInitSettingsState>) => {
        /**
         * Reducers for the 'getInitSettings' thunk.
         */
        builder.addCase(getInitSettings.pending, (state: Draft<IInitSettingsState>) => {
            state.error = null;
            state.loading = true;
        });
        builder.addCase(
            getInitSettings.fulfilled,
            (state: Draft<IInitSettingsState>, action: IAction<IInitSettings>) => {
                state.data = action.payload;
                state.error = null;
                state.loading = false;
            },
        );
        builder.addCase(getInitSettings.rejected, (state, action) => {
            state.error = action.error;
            state.loading = false;
        });
    },
};

/**
 * Init settings as served from backend.
 */
export interface IInitSettings {
    readonly lic: string;
    readonly documentUrl: string;
    readonly language: string;
    readonly testRun?: boolean;
    readonly customFontURL?: string;
}

export interface IInitSettingsState {
    /** Whether a resource is being loaded or not. */
    loading: boolean;

    /** Error that is thrown during resource fetching. */
    error: SerializedError | null;

    /** Data that is fetched from the backend. */
    data: IInitSettings;
}

/**
 * Expose thunk functions as exports.
 */
export const getInitSettings = createAsyncThunk('init/settings', thunks.getInitSettings);

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Create the redux slice.
 */
export const initSettingsSlice = createSlice({
    name: 'initSettings',
    initialState,
    reducers,
    extraReducers: thunks._reducers,
});

export const initSettingsError = (state: IGlobalStoreReducers) => state.initSettings.error;
/**
 * Export slice reducer as default.
 */
export default initSettingsSlice.reducer;
